import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMenuCategories,
  getMenusGroupedByCategory,
  createMenuCategory,
  updateMenuCategory,
  deleteMenuCategory,
  reorderMenuCategories
} from '../services/api/menuCategoriesApi';

// Query key factory
const menuCategoryKeys = {
  all: ['menuCategories'],
  grouped: ['menuCategories', 'grouped'],
};

// Fetch all menu categories
export const useMenuCategories = (options = {}) => {
  return useQuery({
    queryKey: menuCategoryKeys.all,
    queryFn: getMenuCategories,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// Fetch menus grouped by category
export const useMenusGroupedByCategory = (options = {}) => {
  return useQuery({
    queryKey: menuCategoryKeys.grouped,
    queryFn: getMenusGroupedByCategory,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// Create a new menu category
export const useCreateMenuCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMenuCategory,
    onSuccess: () => {
      // Invalidate and refetch categories
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.grouped });
    },
  });
};

// Update a menu category
export const useUpdateMenuCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...updates }) => updateMenuCategory(id, updates),
    onMutate: async ({ id, name }) => {
      const prevGrouped = queryClient.getQueryData(menuCategoryKeys.grouped);
      const prevAll = queryClient.getQueryData(menuCategoryKeys.all);

      if (name) {
        queryClient.setQueryData(menuCategoryKeys.grouped, (old) =>
          old?.map(cat => cat.category_id === id ? { ...cat, category_name: name } : cat)
        );
        queryClient.setQueryData(menuCategoryKeys.all, (old) =>
          old?.map(cat => cat.id === id ? { ...cat, name } : cat)
        );
      }

      return { prevGrouped, prevAll };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevGrouped) queryClient.setQueryData(menuCategoryKeys.grouped, context.prevGrouped);
      if (context?.prevAll) queryClient.setQueryData(menuCategoryKeys.all, context.prevAll);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.grouped });
    },
  });
};

// Reorder menu categories
export const useReorderMenuCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderMenuCategories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.grouped });
    },
  });
};

// Delete a menu category
export const useDeleteMenuCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMenuCategory,
    onSuccess: () => {
      // Invalidate and refetch categories
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: menuCategoryKeys.grouped });
    },
  });
};
